import React, { useEffect, useRef, useState } from 'react';

type PDFDisplayProps = {
  pdfUrl: string | undefined;
};

export const PDFDisplay = ({ pdfUrl }: PDFDisplayProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(0);

  useEffect(() => {
    // Set initial height
    if (containerRef.current) {
      setHeight(containerRef.current.clientHeight);
    }

    // Update height on window resize
    const updateHeight = () => {
      if (containerRef.current) {
        setHeight(containerRef.current.clientHeight);
      }
    };

    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  if (!pdfUrl) {
    return (
      <div className='flex-1 flex flex-col items-center justify-center bg-slate-900 min-h-[500px]'>
        <div className='flex flex-col items-center gap-4'>
          <div className='w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin' />
          <p className='text-slate-400 font-medium animate-pulse'>Loading document...</p>
        </div>
      </div>
    );
  }

  // Robust detection for .doc and .docx files, even with complex query parameters
  const isDoc = !!pdfUrl?.match(/\.(docx|doc)($|\?|&)/i);
  const fullUrl = pdfUrl?.startsWith('http') || pdfUrl?.startsWith('blob:')
    ? pdfUrl
    : `${window.location.origin}${pdfUrl}`;

  // Debug logging to track detection
  if (pdfUrl) {
    console.debug('PDFDisplay Detection:', { pdfUrl, isDoc, fullUrl });
  }

  return (
    <div ref={containerRef} className='bg-black w-full h-full flex flex-col'>
      { isDoc ? (
        <div className='flex-1 flex flex-col'>
          <div className='flex-1 bg-white relative'>
            <iframe
                src={`https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(
                  fullUrl || '',
                )}`}
                width='100%'
                height='100%'
                className='absolute inset-0 w-full h-full'
                frameBorder='0'
                title='Document Viewer'
            >
              <p>Your browser does not support iframes.</p>
            </iframe>
          </div>
        </div>
      ) : (
        <div className='flex-1 relative'>
          <iframe
              src={`${fullUrl}#toolbar=0`}
              width='100%'
              height='100%'
              className='absolute inset-0 w-full h-full'
              frameBorder='0'
              title='PDF Viewer'
          >
            <div className='flex items-center justify-center h-full p-8 text-white text-center'>
              <div>
                <p className='mb-4 text-lg'>
                  Your browser does not support PDF viewing.
                </p>
                <a
                    href={pdfUrl}
                    target='_blank'
                    rel='noreferrer'
                    className='inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700'
                >
                  Download PDF to View
                </a>
              </div>
            </div>
          </iframe>
        </div>
      ) }
    </div>
  );
};
